import React, { useState, useEffect } from 'react';
import { 
    Cpu, 
    DollarSign, 
    Activity, 
    Search, 
    ChevronRight, 
    History,
    TrendingUp,
    Box,
    Layers,
    Calendar,
    RefreshCw
} from 'lucide-react';
import { 
    BarChart, 
    Bar, 
    XAxis, 
    YAxis, 
    CartesianGrid, 
    Tooltip, 
    ResponsiveContainer, 
    AreaChart,
    Area
} from 'recharts';
import { adminManagementService } from '../../services/adminService';
import './AdminAIUsage.css';

function AdminAIUsage() {
    const [stats, setStats] = useState(null);
    const [history, setHistory] = useState([]);
    const [topWorkspaces, setTopWorkspaces] = useState([]);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [dateRange, setDateRange] = useState('30d');

    const getDateParams = (range) => {
        const end = new Date();
        const start = new Date();
        if (range === '24h') start.setHours(start.getHours() - 24);
        else if (range === '7d') start.setDate(start.getDate() - 7);
        else if (range === '30d') start.setDate(start.getDate() - 30);
        else if (range === '90d') start.setDate(start.getDate() - 90);
        else if (range === 'all') return {};
        return { startDate: start.toISOString(), endDate: end.toISOString() };
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const params = getDateParams(dateRange);

            const [statsRes, historyRes, workspacesRes, logsRes] = await Promise.all([
                adminManagementService.getAIUsageStats(params),
                adminManagementService.getAIUsageHistory(params),
                adminManagementService.getTopAIWorkspaces(params),
                adminManagementService.getAIUsageLogs(params)
            ]);

            setStats(statsRes);
            setHistory(historyRes);
            setTopWorkspaces(workspacesRes);
            setLogs(logsRes.logs || []);
        } catch (error) {
            console.error('Failed to fetch AI usage data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [dateRange]);

    const formatCurrency = (val) => `$${(Number(val) || 0).toFixed(4)}`;
    const formatTokens = (val) => {
        if (!val) return '0';
        return val >= 1000000 
            ? `${(val / 1000000).toFixed(2)}M` 
            : val >= 1000 
                ? `${(val / 1000).toFixed(1)}K` 
                : val;
    };

    if (loading && !stats) {
        return (
            <div className="ai-usage__loading">
                <div className="ai-usage__spinner"></div>
            </div>
        );
    }

    return (
        <div className="ai-usage">
            <header className="ai-usage__header">
                <div className="ai-usage__title-group">
                    <h1>AI Intelligence Center</h1>
                    <p>Monitor token consumption, model performance, and infrastructure costs.</p>
                </div>

                <div className="ai-usage__controls">
                    <div className="ai-usage__date-picker">
                        <Calendar size={16} />
                        <select 
                            className="ai-usage__select"
                            value={dateRange}
                            onChange={(e) => setDateRange(e.target.value)}
                        >
                            <option value="24h">Last 24 Hours</option>
                            <option value="7d">Last 7 Days</option>
                            <option value="30d">Last 30 Days</option>
                            <option value="90d">Last 90 Days</option>
                            <option value="all">All Time</option>
                        </select>
                    </div>
                    
                    <button 
                        onClick={fetchData}
                        className="ai-usage__date-picker"
                        title="Refresh Data"
                    >
                        <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                    </button>
                </div>
            </header>

            {/* Overview Stats */}
            <div className="ai-usage__stats-grid">
                <div className="ai-usage__stat-card ai-usage__stat-card--blue">
                    <div className="ai-usage__stat-label">Total Revenue Value</div>
                    <div className="ai-usage__stat-value">
                        <DollarSign size={24} color="#38bdf8" />
                        {formatCurrency(stats?.summary?.totalCost)}
                    </div>
                </div>
                
                <div className="ai-usage__stat-card ai-usage__stat-card--purple">
                    <div className="ai-usage__stat-label">Tokens Consumed</div>
                    <div className="ai-usage__stat-value">
                        <Cpu size={24} color="#a855f7" />
                        {formatTokens(stats?.summary?.totalTokens)}
                    </div>
                </div>

                <div className="ai-usage__stat-card ai-usage__stat-card--emerald">
                    <div className="ai-usage__stat-label">AI Requests</div>
                    <div className="ai-usage__stat-value">
                        <Activity size={24} color="#10b981" />
                        {Number(stats?.summary?.totalRequests || 0).toLocaleString()}
                    </div>
                </div>

                <div className="ai-usage__stat-card ai-usage__stat-card--amber">
                    <div className="ai-usage__stat-label">Avg Cost / Request</div>
                    <div className="ai-usage__stat-value">
                        <TrendingUp size={24} color="#f59e0b" />
                        {formatCurrency(stats?.summary?.totalRequests > 0 ? stats.summary.totalCost / stats.summary.totalRequests : 0)}
                    </div>
                </div>
            </div>

            <div className="ai-usage__charts-section">
                {/* Main Chart */}
                <div className="ai-usage__chart-container">
                    <div className="ai-usage__chart-header">
                        <div className="ai-usage__chart-title">
                            <h3>Consumption History</h3>
                            <p>Daily revenue value from AI token usage</p>
                        </div>
                        <History size={20} color="#64748b" />
                    </div>
                    <div style={{ height: '300px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={history}>
                                <defs>
                                    <linearGradient id="colorCost" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#38bdf8" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="#38bdf8" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                                <XAxis 
                                    dataKey="date" 
                                    stroke="#475569" 
                                    fontSize={10} 
                                    tickFormatter={(val) => new Date(val).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                />
                                <YAxis stroke="#475569" fontSize={10} tickFormatter={(val) => `$${val}`} />
                                <Tooltip 
                                    contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: '8px' }}
                                    itemStyle={{ color: '#fff', fontSize: '12px' }}
                                    labelStyle={{ color: '#64748b', fontSize: '10px', marginBottom: '4px' }}
                                />
                                <Area type="monotone" dataKey="cost" stroke="#38bdf8" strokeWidth={2} fillOpacity={1} fill="url(#colorCost)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Model Breakdown */}
                <div className="ai-usage__chart-container">
                    <div className="ai-usage__chart-header">
                        <div className="ai-usage__chart-title">
                            <h3>Model Distribution</h3>
                            <p>Usage by Gemini models</p>
                        </div>
                    </div>
                    <div className="ai-usage__distribution-list">
                        {stats?.models?.map((m) => (
                            <div key={m.model} className="ai-usage__distribution-item">
                                <div className="ai-usage__item-header">
                                    <span className="ai-usage__model-name">{m.model}</span>
                                    <span style={{ color: '#fff', fontWeight: 700 }}>{formatCurrency(m.cost)}</span>
                                </div>
                                <div className="ai-usage__progress-bg">
                                    <div 
                                        className="ai-usage__progress-fill" 
                                        style={{ width: `${(m.cost / stats.summary.totalCost) * 100}%` }}
                                    />
                                </div>
                                <div className="ai-usage__item-header" style={{ fontSize: '10px', color: '#64748b' }}>
                                    <span>{formatTokens(m.tokens)} Tokens</span>
                                    <span>{m.requests} Req</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="ai-usage__charts-section">
                {/* Top Workspaces */}
                <div className="ai-usage__chart-container">
                    <div className="ai-usage__chart-header">
                        <div className="ai-usage__chart-title">
                            <h3>Heavy Consumers</h3>
                            <p>Top workspaces by revenue contribution</p>
                        </div>
                        <Box size={20} color="#64748b" />
                    </div>
                    <div className="ai-usage__distribution-list">
                        {topWorkspaces.map((ws, idx) => (
                            <div key={ws.workspaceId} className="flex items-center gap-4 p-2 rounded-lg hover:bg-white/5 transition-colors group">
                                <div style={{ 
                                    width: '32px', 
                                    height: '32px', 
                                    borderRadius: '50%', 
                                    background: 'rgba(255,255,255,0.05)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '10px',
                                    fontWeight: 900,
                                    color: '#64748b'
                                }}>
                                    {idx + 1}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <p style={{ fontSize: '14px', fontWeight: 700, color: '#fff', margin: 0 }}>{ws.workspaceName}</p>
                                    <p style={{ fontSize: '10px', color: '#64748b', margin: 0, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        {ws.totalRequests} API Calls
                                    </p>
                                </div>
                                <div style={{ textRight: 'right' }}>
                                    <p style={{ fontSize: '14px', fontWeight: 900, color: '#fff', margin: 0 }}>{formatCurrency(ws.totalCost)}</p>
                                    <p style={{ fontSize: '10px', color: '#64748b', margin: 0 }}>{formatTokens(ws.totalTokens)}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Action Breakdown */}
                <div className="ai-usage__chart-container">
                    <div className="ai-usage__chart-header">
                        <div className="ai-usage__chart-title">
                            <h3>Action Intelligence</h3>
                            <p>Most expensive AI features</p>
                        </div>
                        <Layers size={20} color="#64748b" />
                    </div>
                    <div style={{ height: '300px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={stats?.actions} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} />
                                <XAxis type="number" stroke="#475569" fontSize={10} hide />
                                <YAxis dataKey="action" type="category" stroke="#475569" fontSize={10} width={100} />
                                <Tooltip 
                                    contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #1e293b', borderRadius: '8px' }}
                                    itemStyle={{ color: '#fff', fontSize: '12px' }}
                                />
                                <Bar dataKey="cost" fill="#a855f7" radius={[0, 4, 4, 0]} barSize={16} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Raw Logs Table */}
            <div className="ai-usage__table-card">
                <div className="ai-usage__table-header">
                    <div className="ai-usage__chart-title">
                        <h3>Operational Stream</h3>
                        <p>Live feed of AI token usage events</p>
                    </div>
                    <div className="ai-usage__search">
                        <Search size={16} className="ai-usage__search-icon" />
                        <input 
                            type="text" 
                            placeholder="Filter logs..." 
                            className="ai-usage__input"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                </div>
                <div className="ai-usage__table-wrapper">
                    <table className="ai-usage__table">
                        <thead>
                            <tr>
                                <th>Timestamp</th>
                                <th>Workspace</th>
                                <th>Action</th>
                                <th>Model</th>
                                <th style={{ textAlign: 'right' }}>Tokens</th>
                                <th style={{ textAlign: 'right' }}>Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.filter(log => 
                                log.action.toLowerCase().includes(search.toLowerCase()) || 
                                log.workspace?.name?.toLowerCase().includes(search.toLowerCase())
                            ).map((log) => (
                                <tr key={log.id}>
                                    <td>
                                        <div className="ai-usage__timestamp-time">{new Date(log.createdAt).toLocaleTimeString()}</div>
                                        <div className="ai-usage__timestamp-date">{new Date(log.createdAt).toLocaleDateString()}</div>
                                    </td>
                                    <td>
                                        <span style={{ fontWeight: 700, color: '#cbd5e1' }}>{log.workspace?.name || 'Unknown'}</span>
                                    </td>
                                    <td>
                                        <span className="ai-usage__badge">{log.action.replace(/_/g, ' ')}</span>
                                    </td>
                                    <td>
                                        <span style={{ fontFamily: 'monospace', fontSize: '10px' }}>{log.model}</span>
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        <div style={{ fontWeight: 700, color: '#fff' }}>{log.totalTokens.toLocaleString()}</div>
                                        <div style={{ fontSize: '10px', color: '#64748b' }}>I:{log.promptTokens} O:{log.completionTokens}</div>
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        <span className="ai-usage__cost--positive">{formatCurrency(log.cost)}</span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default AdminAIUsage;
